import { CaseClient, SampleCreateFromExistingModel, EvidenceMarkerModel, SampleModel, SampleClient, SampleMarkerModel } from '@app/api2/api';
import { Field } from 'formik';
import * as React from 'react';
import * as yup from "yup";
import { Checkbox, Container, Form } from 'semantic-ui-react';
import { BPFormikFieldCheckbox } from '@common/BPCheckbox';
import { BPColumn, BPReactTable } from '@common/BPReactTable';
import { InfoBox } from '@common/InfoBox';
import { PageHeader } from '@common/PageHeader';
import { insertComponentsIntoText } from '@common/Utils';
import { BPForm } from '@common/BPForm';
import { RefObject } from 'react';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { BPCheckbox } from '@/components/bp/bpcheckbox';
import { BPContainer } from '@/components/bp/bpcontainer';

export interface ISampleCopyProps {
    sampleId: number;
    labId: number;
    caseId?: number;
}

export interface ISampleCopyState {
    sample: SampleModel;
    selectAll: boolean;
    caseUrl: string;
    detailsUrl: string;
    formData: SampleCreateFromExistingModel;
}

export default class SampleCopy extends React.Component<ISampleCopyProps, ISampleCopyState> {
    private caseClient = new CaseClient();
    private sampleClient = new SampleClient();
    private formikRef: React.Ref<BPForm<SampleCreateFromExistingModel>>;
    private createSchema: yup.SchemaOf<SampleCreateFromExistingModel> = yup.object().shape({
        name: yup.string().required().label(window.translation.common.name),
        markers: yup.array().of(yup.string()).min(1).label(window.translation.kit.markers),
    });

    constructor(props: ISampleCopyProps) {
        super(props);
        this.formikRef = React.createRef();
        // this.formikRef.current.formik.current.setErrors()

        this.state = {
            selectAll: false,
            sample: null,
            caseUrl: '',
            detailsUrl: '',
            formData: {
                name: '',
                markers: []
            }
        }
    }

    async componentDidMount() {
        // const kits = await this.caseClient.getCaseKits(this.props.labId, this.props.caseId);

        const sample = await this.sampleClient.getDetails(this.props.labId, this.props.caseId ?? 0, this.props.sampleId);
        // console.log(sample)

        this.setState({
            selectAll: true,
            sample: sample.sample,
            caseUrl: sample.caseUrl,
            detailsUrl: sample.detailsUrl,
            formData: {
                name: sample.sample.name,
                markers: sample.sample.markers.map(m => m.markerName)
            }
        });
    }

    public render() {
        const selectAllCheckboxes = isSelected => {
            this.setState({
                selectAll: isSelected,
                formData: {
                    ...(this.formikRef as RefObject<any>).current.formik.current.values,
                    markers: isSelected ? this.state.sample.markers.map(x => x.markerName) : []
                }

            })
        };

        const columns: BPColumn<SampleMarkerModel>[] = [
            {
                id: 'checkbox',
                Header: <BPCheckbox checked={this.state.selectAll} onChange={() => selectAllCheckboxes(!this.state.selectAll)} />,
                Cell: cell => <BPFormikFieldCheckbox key={cell.row.original.markerName} name="markers" value={cell.row.original.markerName} />,
                tableCellProps: {
                    collapsing: true,
                }

            },
            { accessor: 'markerName', Header: window.translation.kit.marker },
        ];

        const otherColumns: BPColumn[] = [
            ...(this.state.sample ? Array(2).keys() : [])
        ].map(alleleIndex => {
            return [
                {
                    id: `al${alleleIndex}`,
                    accessor: 'alleles',
                    Header: window.translation.sampleView.alleleWithNumber.replace('{alleleNumber}', (alleleIndex + 1).toString()),
                    Cell: cellProps => <span>{cellProps.value.length > alleleIndex ? cellProps.value[alleleIndex].allele : ''}</span>
                },
                // {
                //     id: `hi${alleleIndex}`,
                //     accessor: 'alleles',
                //     Header: window.translation.evidenceView.heightWithNumber.replace('{heightNumber}', (alleleIndex + 1).toString()),
                //     Cell: cellProps => <span>{cellProps.value.length > alleleIndex ? cellProps.value[alleleIndex].height : ''}</span>
                // }
            ]
        }).reduce((prev, curr) => { prev.push(curr[0]); return prev; }, []);

        const cols = [...columns, ...otherColumns]
        const caseName = this.state?.sample?.cases?.length > 0 ? this.state.sample.cases[0].name : ''

        return (
            <>
                {this.state.sample &&
                    <BPContainer>
                        <PageHeader header={insertComponentsIntoText(window.translation.sample.createFromExistingTitle, {
                            '{caseName}': <a key='caseUrlLink' href={this.state.caseUrl}>{caseName}</a>
                        })} />
                        <BPForm
                            initialValues={this.state.formData}
                            ref={this.formikRef}
                            onCancel={() => {
                                window.location.assign(this.state.detailsUrl);
                            }}
                            vaildationSchema={this.createSchema}
                            onSubmit={(values) => this.save(values)}>
                            {(props) => (
                                <>
                                    <InfoBox
                                        items={[
                                            { label: window.translation.sample.originalName, description: this.state.sample.name },
                                            { label: window.translation.common.kit, description: this.state.sample.kitName },
                                            { label: window.translation.common.name, description: <Field as={Form.Field} control={Form.Input} name="name" error={props.errors.name && props.touched.name}></Field> },
                                        ]} />
                                    {/* <BPSection header={window.translation.sample.selectMarkersForInclude}> */}
                                    <BPReactTable
                                        compact
                                        disablePagination
                                        disableGlobalFilter
                                        disableSortBy
                                        stickyHeader
                                        celled
                                        data={this.state.sample.markers}
                                        columns={cols}
                                    />
                                    {/* </BPSection> */}
                                </>
                            )}
                        </BPForm>
                    </BPContainer>
                }
            </>
        );
    }

    async save(values: { name?: string; markers?: string[]; }) {
        console.log(values)
        let result = await this.sampleClient.createFromExisting(this.props.labId, this.props.caseId ?? 0, this.props.sampleId, {
            name: values.name,
            markers: values.markers,
        })

        if (result.successful) {
            if (this.props.caseId)
                window.location.assign(buildUrl(AppRoutes.SampleDetails, { labId: this.props.labId, caseId: this.props.caseId, sampleId: result.newSample[0].id }))
            else
                window.location.assign(buildUrl(AppRoutes.SampleDetailsWithoutCase, { labId: this.props.labId, sampleId: result.newSample[0].id }))
        }
    }
}

